import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import ApiPlayground from '../components/ApiPlayground';
import Footer from '../components/Footer';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('curl');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, usageRes, keysRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/keys/usage'),
        api.get('/keys')
      ]);

      setUser(userRes.data.user);
      setUsage(usageRes.data);
      setApiKeys(keysRes.data.apiKeys);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  const usagePercentage = usage?.usage ? (usage.usage.total / usage.usage.quota) * 100 : 0;

  // Generate code examples for different languages
  // Copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Code copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy code');
    });
  };

  const getCodeExample = (language, apiKey) => {
    const maskedKey = apiKey
      ? apiKey.substring(0, 15) + '...' + apiKey.substring(apiKey.length - 4)
      : 'YOUR_API_KEY';

    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

    const examples = {
      curl: `curl -X POST ${baseURL}/api/screenshot \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${maskedKey}" \\
  -d '{"url": "https://example.com"}' \\
  --output screenshot.png`,

      node: `const axios = require('axios');
const fs = require('fs');

const response = await axios.post(
  '${baseURL}/api/screenshot',
  { url: 'https://example.com' },
  {
    headers: { 'X-API-Key': '${maskedKey}' },
    responseType: 'arraybuffer'
  }
);

fs.writeFileSync('screenshot.png', response.data);`,

      python: `import requests

response = requests.post(
    '${baseURL}/api/screenshot',
    json={'url': 'https://example.com'},
    headers={'X-API-Key': '${maskedKey}'}
)

with open('screenshot.png', 'wb') as f:
    f.write(response.content)`,

      php: `<?php
$ch = curl_init('${baseURL}/api/screenshot');
curl_setopt_array($ch, [
    CURLOPT_HTTPHEADER => [
        'X-API-Key: ${maskedKey}',
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode(['url' => 'https://example.com']),
    CURLOPT_RETURNTRANSFER => true
]);

$imageData = curl_exec($ch);
curl_close($ch);

file_put_contents('screenshot.png', $imageData);`,

      ruby: `require 'net/http'
require 'json'

uri = URI('${baseURL}/api/screenshot')
request = Net::HTTP::Post.new(uri)
request['X-API-Key'] = '${maskedKey}'
request['Content-Type'] = 'application/json'
request.body = { url: 'https://example.com' }.to_json

response = Net::HTTP.start(uri.hostname, uri.port) do |http|
  http.request(request)
end

File.write('screenshot.png', response.body)`,

      go: `package main

import (
    "bytes"
    "encoding/json"
    "io"
    "net/http"
    "os"
)

func main() {
    body, _ := json.Marshal(map[string]string{"url": "https://example.com"})

    req, _ := http.NewRequest("POST", "${baseURL}/api/screenshot", bytes.NewBuffer(body))
    req.Header.Set("X-API-Key", "${maskedKey}")
    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    resp, _ := client.Do(req)
    defer resp.Body.Close()

    file, _ := os.Create("screenshot.png")
    defer file.Close()
    io.Copy(file, resp.Body)
}`
    };

    return examples[language] || examples.curl;
  };

  return (
    <>
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Welcome back, {user?.name}!</p>
      </div>

      {/* Email Verification Banner */}
      <EmailVerificationBanner user={user} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Current Plan */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase">Current Plan</h3>
          <p className="text-3xl font-bold text-indigo-600 mt-2 capitalize">{user?.plan || 'Free'}</p>
          <div className="mt-2 text-sm">
            <Link to="/pricing" className="text-indigo-600 hover:text-indigo-500">
              Change plan →
            </Link>
          </div>
        </div>

        {/* Usage This Month */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase">Usage This Month</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">
            {usage?.usage?.total || 0} / {usage?.usage?.quota || 0}
          </p>
          <div className="mt-3 bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
          {!user?.emailVerified && (
            <p className="text-xs text-gray-600 mt-2">
              <span className="text-orange-600 font-medium">
                {Math.max(0, 10 - (user?.apiUsage || 0))} requests
              </span> remaining until email verification required
            </p>
          )}
        </div>

        {/* API Keys */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase">API Keys</h3>
          <p className="text-3xl font-bold text-gray-900 mt-2">{apiKeys.length}</p>
          <Link to="/api-keys" className="text-sm text-indigo-600 hover:text-indigo-500 mt-2 inline-block">
            Manage keys →
          </Link>
        </div>
      </div>

      {/* API Playground - Try It Out */}
      <div className="mb-8">
        <ApiPlayground apiKey={apiKeys[0]?.key} onUsageUpdate={fetchData} />
      </div>

      {/* Quick Start */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Start</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Your API Key</h3>
            {apiKeys.length > 0 ? (
              <div>
                <div className="bg-gray-50 rounded p-3 font-mono text-sm break-all">
                  {apiKeys[0].key.substring(0, 15)}...{apiKeys[0].key.substring(apiKeys[0].key.length - 4)}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  🔒 Full key is only shown once after creation. <Link to="/api-keys" className="text-indigo-600 hover:text-indigo-500">View in API Keys →</Link>
                </p>
              </div>
            ) : (
              <p className="text-gray-600">No API keys yet. <Link to="/api-keys" className="text-indigo-600 hover:text-indigo-500">Create one →</Link></p>
            )}
          </div>

          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
              <h3 className="font-medium text-gray-900">Example Request</h3>

              {/* Language Selector */}
              <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
                {['curl', 'node', 'python', 'php', 'ruby', 'go'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => setSelectedLanguage(lang)}
                    className={`px-4 py-2 text-xs sm:text-sm font-medium rounded transition-colors ${
                      selectedLanguage === lang
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {lang === 'node' ? 'Node.js' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 rounded p-4 text-sm overflow-x-auto">
                {getCodeExample(selectedLanguage, apiKeys[0]?.key)}
              </pre>
              <button
                onClick={() => copyToClipboard(getCodeExample(selectedLanguage, apiKeys[0]?.key))}
                className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors group"
                title="Copy to clipboard"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {apiKeys.length > 0
                ? 'Replace the masked key with your actual API key from the API Keys page.'
                : 'Create an API key first to see personalized code examples.'}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Usage */}
      {usage?.breakdown && usage.breakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Usage Breakdown</h2>
          <div className="space-y-3">
            {usage.breakdown.map((item, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b">
                <span className="text-gray-700">{item.endpoint}</span>
                <span className="font-semibold text-gray-900">{item.count} requests</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    <Footer className="hidden md:block" />
    </>
  );
}

export default Dashboard;